import dayjs from 'dayjs';
import { getDate, isAfterDate } from '@zola-helpers/client/dist/es/util/dateUtils';

export const dataFormat = 'YYYY-MM-DD';
export const inputFormat = 'MM/DD/YYYY';
// pre: Date object
// post: string of 'MM/DD/YYYY'
export const defaultFormat = (date?: Date): string => {
  return dayjs(date).format(inputFormat);
};

// pre: string of format 'MM/DD/YYYY'
// post: if valid, Date object; if invalid, null
export const defaultParse = (date?: string): Date | undefined => {
  if (dayjs(date, inputFormat, true).isValid()) {
    return dayjs(date, inputFormat).toDate();
  }
  return undefined;
};

const isOneMonthRegex = /^[1-9]+$/;
const isNumRegex = /^\d+$/;
/**
 * @param monthStr A two digit month string (1-indexed)
 * @returns A number 0-11 representing the monthIndex, or null for invalid inputs
 */
const parseTwoDigitMonth = (monthStr: string): number | null => {
  // console.log('regextest', monthStr, isNumRegex.test(monthStr));
  if (isNumRegex.test(monthStr)) {
    const monthIndex = Number(monthStr) - 1;
    // console.log(monthIndex);
    if (monthIndex < 0 || monthIndex > 11) {
      return null;
    }
    return monthIndex;
  }
  return null;
};
const parseTwoDigitDate = (dateStr: string): number | null => {
  if (isNumRegex.test(dateStr)) {
    const date = Number(dateStr);
    if (date <= 0 || date > 31) {
      return null;
    }
    return date;
  }
  return null;
};

/**
 * @param date A user input string of format 'MM/DD/YYYY', accepts partial implentations (e.g. "01" returns 01/01/YYYY in the current year)
 * @returns If valid, Date object; if invalid, undefined
 * NOTE: does not validate calendar days... all days 1 - 31 are valid.  So 02/31 will be valid.
 * */
export const calendarParser = (date?: string): Date | undefined => {
  const dayString = dayjs().format('MM/DD/YYYY');
  const today = dayjs(dayString);
  const todayYear = today.year();
  if (!date) {
    return undefined;
  }
  if (date === '0') {
    return today.toDate();
  }
  if (date.length === 1 && isOneMonthRegex.test(date)) {
    const month = Number(date) - 1;
    return new Date(todayYear, month, 1, 0, 0, 0);
  }
  let monthIndex = null;
  if (date.length >= 2) {
    const monthStr = date.slice(0, 2);
    monthIndex = parseTwoDigitMonth(monthStr);
  }
  if (date.length >= 2 && date.length <= 4) {
    if (monthIndex !== null) {
      return new Date(todayYear, monthIndex, 1, 0, 0, 0);
    }
    return undefined;
  }
  if (date.length === 5 || date.length === 6) {
    const dayString = date.slice(3, 5);
    const day = parseTwoDigitDate(dayString);

    if (monthIndex !== null && day) {
      return new Date(todayYear, monthIndex, day, 0, 0, 0);
    }
    return undefined;
  }
  let parsedDate = dayjs(date, 'MM/DD/YY', true);
  if (parsedDate.isValid()) {
    return parsedDate.toDate();
  }
  // For full years, parse with the formatter
  parsedDate = dayjs(date, 'MM/DD/YYYY', true);
  if (parsedDate.isValid()) {
    return parsedDate.toDate();
  }
  return undefined;
};

/**
 * Tool for improving the DayPicker calendar view while a user enters a date.
 * Takes a date object and returns the the next valid date after now as a Date object
 */
export const getNextValidDate = (inputDate: Date | undefined): Date | undefined => {
  if (!inputDate) {
    return undefined;
  }

  const dayString = dayjs().format('MM/DD/YYYY');
  const today = dayjs(dayString).toDate();
  const userInputDateString = dayjs(inputDate).format('MM/DD/YYYY');
  const userInputDay = dayjs(userInputDateString).toDate();
  if (isAfterDate(userInputDay, today)) {
    return userInputDay;
  }
  // Set the year to this year for comparison
  userInputDay.setFullYear(today.getFullYear());
  // If the user input is after today... the current year is valid, return that date.
  if (isAfterDate(userInputDay, today)) {
    return userInputDay;
  }
  // If the user input is before now (excluding year)... return the next valid date k eeping the month/day params.
  return getDate(userInputDay.setFullYear(userInputDay.getFullYear() + 1));
};
